import { useState, useEffect, useRef, useMemo } from "react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { api, tradeAuth, connectToDeriv, toastApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Shield, Volume2, VolumeX, Square as StopIcon } from "lucide-react";
import { GAME_COVERS } from "@/lib/casinoArt";
import { StakeSelector, MIN_STAKE } from "@/components/casino/StakeSelector";
import { CoinShower } from "@/components/casino/CoinShower";
import { isMuted, setMutedGlobal, buzz, BUZZ } from "@/lib/fx";
import { LuckyHourBanner } from "@/components/casino/LuckyHourBanner";
import { GUARD_PCT, TARGET_LOSS_RUN, WIN_RATIO, recoveryStake, survivableLosses, safeBaseFor } from "@/lib/stakeMath";

/* ------------------------------------------------------------------ */
/*  Mini WebAudio synth — no asset files, zero network cost           */
/* ------------------------------------------------------------------ */
let _audioCtx = null;
function getCtx() {
  if (!_audioCtx) {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return null; }
  }
  if (_audioCtx.state === "suspended") _audioCtx.resume().catch(() => {});
  return _audioCtx;
}
function beep(freq, duration, type = "sine", gain = 0.08, slide = null) {
  const ctx = getCtx(); if (!ctx) return;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime);
  if (slide) osc.frequency.exponentialRampToValueAtTime(slide, ctx.currentTime + duration);
  g.gain.setValueAtTime(gain, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
  osc.connect(g); g.connect(ctx.destination);
  osc.start(); osc.stop(ctx.currentTime + duration);
}
function noiseBurst(duration, gain = 0.1) {
  const ctx = getCtx(); if (!ctx) return;
  const buf = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = ctx.createBufferSource();
  const g = ctx.createGain();
  src.buffer = buf;
  g.gain.value = gain;
  src.connect(g); g.connect(ctx.destination);
  src.start();
}
const SFX = {
  whistle: () => { beep(2100, 0.14, "sine", 0.07); setTimeout(() => beep(2400, 0.1, "sine", 0.06), 130); },
  kick:    () => { beep(90, 0.08, "square", 0.14, 50); noiseBurst(0.07, 0.08); },
  swish:   () => { noiseBurst(0.25, 0.06); beep(1800, 0.18, "sine", 0.05, 900); },
  flip:    () => { beep(500, 0.04, "square", 0.08); setTimeout(() => beep(300, 0.05, "square", 0.07), 60); },
  chest:   () => { beep(880, 0.12, "triangle", 0.07); setTimeout(() => beep(1320, 0.18, "triangle", 0.07), 110); setTimeout(() => beep(1760, 0.22, "triangle", 0.07), 240); },
  win:     () => { beep(600, 0.1, "triangle", 0.1); setTimeout(() => beep(900, 0.12, "triangle", 0.1), 110); setTimeout(() => beep(1200, 0.18, "triangle", 0.1), 240); },
  coins:   () => { for (let i = 0; i < 8; i++) setTimeout(() => beep(2200 + Math.random() * 900, 0.06, "triangle", 0.05), i * 70); },
  jackpot: () => {
    [523, 659, 784, 1047, 1319].forEach((f, i) => setTimeout(() => beep(f, 0.22, "square", 0.06), i * 110));
    for (let i = 0; i < 18; i++) setTimeout(() => beep(2000 + Math.random() * 1400, 0.05, "triangle", 0.045), 550 + i * 55);
  },
  loss:    () => { beep(240, 0.18, "sawtooth", 0.09, 120); setTimeout(() => beep(160, 0.25, "sawtooth", 0.08, 100), 160); },
};
const _muted = isMuted();
function playSfx(name) { if (isMuted()) return; (SFX[name] || (() => {}))(); }
const setMuted = setMutedGlobal;

/* -- Share Clip: generate a 1080×1080 PNG of the win and offer download/share -- */
function shareWin({ amount, stake, game, label }) {
  const svg = `
<svg xmlns='http://www.w3.org/2000/svg' width='1080' height='1080' viewBox='0 0 1080 1080'>
  <defs>
    <linearGradient id='bg' x1='0' x2='1' y1='0' y2='1'>
      <stop offset='0' stop-color='#0a0514'/>
      <stop offset='1' stop-color='#2a0a30'/>
    </linearGradient>
    <radialGradient id='glow' cx='0.5' cy='0.5' r='0.6'>
      <stop offset='0' stop-color='rgba(0,255,157,0.35)'/>
      <stop offset='1' stop-color='transparent'/>
    </radialGradient>
  </defs>
  <rect width='1080' height='1080' fill='url(#bg)'/>
  <rect width='1080' height='1080' fill='url(#glow)'/>
  <text x='540' y='140' text-anchor='middle' font-family='Impact, Arial Black' font-size='52' fill='#E8B54A'>NAEVO ARCADE</text>
  <text x='540' y='210' text-anchor='middle' font-family='Impact, Arial Black' font-size='36' fill='rgba(255,255,255,0.6)'>${(game || "").toUpperCase()}</text>
  <text x='540' y='460' text-anchor='middle' font-family='Impact, Arial Black' font-size='220' fill='#00FF9D' style='text-shadow:0 0 40px rgba(0,255,157,0.6)'>${label || "WIN!"}</text>
  <text x='540' y='660' text-anchor='middle' font-family='Impact, Arial Black' font-size='180' fill='#00FF9D'>+$${(amount || 0).toFixed(2)}</text>
  <text x='540' y='740' text-anchor='middle' font-family='IBM Plex Mono, monospace' font-size='38' fill='rgba(255,255,255,0.55)'>on a $${(stake || 0).toFixed(2)} stake · 1 tap · 1 win</text>
  <rect x='140' y='860' width='800' height='120' rx='60' fill='rgba(232,181,74,0.15)' stroke='#E8B54A' stroke-width='3'/>
  <text x='540' y='938' text-anchor='middle' font-family='IBM Plex Mono, monospace' font-size='44' fill='#E8B54A'>play.naevo.ai</text>
</svg>`.trim();
  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1080; canvas.height = 1080;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    canvas.toBlob(async (pngBlob) => {
      if (!pngBlob) return;
      const file = new File([pngBlob], "naevo-win.png", { type: "image/png" });
      // Try the native share sheet first (mobile)
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: `+$${(amount || 0).toFixed(2)} on Naevo Bot — try it: naevobot.com` });
          return;
        }
      } catch {}
      // Fallback — download
      const dl = URL.createObjectURL(pngBlob);
      const a = document.createElement("a");
      a.href = dl; a.download = `naevo-win-${Date.now()}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(dl), 2000);
    }, "image/png");
  };
  img.src = url;
}

/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Each game is mapped to its own Deriv contract family so the arcade */
/*  feels diverse: digits (even/odd), over_under (digits 5-9 vs 0-4),   */
/*  and rise_fall (tick direction). The signal engine scans the right  */
/*  stat for the game's family.                                        */
/* ------------------------------------------------------------------ */
const GAMES = [
  {
    key: "penalty",
    title: "Penalty Shootout",
    tag: "50/50 · ×1.95",
    desc: "Shoot left or right. Beat the keeper to score.",
    family: "digits",
    left:  { pick: "left",  label: "SHOOT LEFT",  side: "EVEN", hint: "digits 0·2·4·6·8" },
    right: { pick: "right", label: "SHOOT RIGHT", side: "ODD",  hint: "digits 1·3·5·7·9" },
    actionWord: "SHOOT",
    scoreL: "Goals",
    scoreR: "Saved",
  },
  {
    key: "flip",
    title: "Red / Black Flip",
    tag: "OVER / UNDER · ×1.95",
    desc: "Call the card. Red (high) or Black (low).",
    family: "over_under",
    left:  { pick: "red",   label: "RED CARD",   side: "OVER",  hint: "digits 5·6·7·8·9" },
    right: { pick: "black", label: "BLACK CARD", side: "UNDER", hint: "digits 0·1·2·3·4" },
    actionWord: "FLIP",
    scoreL: "Reds",
    scoreR: "Blacks",
  },
  {
    key: "hoop",
    title: "Hoop Shot",
    tag: "RISE / FALL · ×1.95",
    desc: "Buzzer beater. Bank up or stuff it down.",
    family: "rise_fall",
    left:  { pick: "swish", label: "SHOOT UP",   side: "RISE", hint: "price ticks up" },
    right: { pick: "bank",  label: "SHOOT DOWN", side: "FALL", hint: "price ticks down" },
    actionWord: "SHOOT",
    scoreL: "Swish",
    scoreR: "Bricks",
  },
  {
    key: "treasure",
    title: "Treasure Duel",
    tag: "OVER / UNDER · ×1.95",
    desc: "High-value or low-value chest. Pick the right one.",
    family: "over_under",
    left:  { pick: "left",  label: "HIGH CHEST", side: "OVER",  hint: "digits 5·6·7·8·9" },
    right: { pick: "right", label: "LOW CHEST",  side: "UNDER", hint: "digits 0·1·2·3·4" },
    actionWord: "OPEN",
    scoreL: "Gold",
    scoreR: "Empty",
  },
  {
    key: "dice",
    title: "Dice Duel",
    tag: "50/50 · ×1.95",
    desc: "Shake the die. Call even or odd before it settles.",
    family: "digits",
    left:  { pick: "even",  label: "ROLL EVEN", side: "EVEN", hint: "digits 0·2·4·6·8" },
    right: { pick: "odd",   label: "ROLL ODD",  side: "ODD",  hint: "digits 1·3·5·7·9" },
    actionWord: "ROLL",
    scoreL: "Lucky",
    scoreR: "Busted",
  },
  {
    key: "wheel",
    title: "Lucky Wheel",
    tag: "RISE / FALL · ×1.95",
    desc: "Spin the wheel. Lock in up or down.",
    family: "rise_fall",
    left:  { pick: "green", label: "SPIN UP",   side: "RISE", hint: "wheel climbs" },
    right: { pick: "red",   label: "SPIN DOWN", side: "FALL", hint: "wheel drops" },
    actionWord: "SPIN",
    scoreL: "Hits",
    scoreR: "Misses",
  },
];

export default function Arcade() {
  const [active, setActive] = useState(null);
  return (
    <div>
      <SiteNav />
      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="text-xs uppercase tracking-[0.35em] text-gold/80">Naevo Casino · Arcade</div>
        <h1 className="font-casino text-gold-grad text-4xl sm:text-5xl lg:text-6xl mt-2">
          Six tables. Real payouts.
        </h1>
        <p className="text-white/60 mt-2 max-w-2xl">
          Every tap is a real Deriv contract under the hood — Even/Odd, Over/Under, or Rise/Fall. The engine scans 10 markets and auto-locks the strongest setup for whichever game you're on. Capped at 30 plays per game per hour so a cold streak can't drain you.
        </p>
        <div className="mt-5"><LuckyHourBanner compact /></div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          {GAMES.map((g) => (
            <button
              key={g.key}
              onClick={() => setActive(g)}
              data-testid={`arcade-open-${g.key}`}
              className="group text-left casino-tile rounded-2xl overflow-hidden relative h-64 sm:h-72"
            >
              <CardArt gameKey={g.key} />
              <div className="relative p-6 h-full flex flex-col justify-end">
                <div className="marquee-badge w-fit">{g.tag}</div>
                <div className="font-casino text-gold-grad text-3xl mt-3">{g.title}</div>
                <div className="text-sm text-white/70 mt-1">{g.desc}</div>
                <div className="mt-4 btn-gold rounded-full px-5 py-2 text-sm w-fit">Play now →</div>
              </div>
            </button>
          ))}
        </div>

        <AnimatePresence>
          {active && <GameModal game={active} onClose={() => setActive(null)} />}
        </AnimatePresence>
      </main>
      <SiteFooter />
    </div>
  );
}

/* ================================================================== */
/*                             GAME MODAL                             */
/* ================================================================== */
function GameModal({ game, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  // Signal engine — scans 10 markets every ~4s while modal is open
  const [signal, setSignal] = useState(null);
  const [signalLoading, setSignalLoading] = useState(true);

  // Game state
  const [animState, setAnimState] = useState("idle");
  const [result, setResult] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [autoMode, setAutoMode] = useState("off"); // off | 3 | 5 | 10 | 30
  const [autoProgress, setAutoProgress] = useState({ played: 0, total: 0, pnl: 0 });
  const [autoRunning, setAutoRunning] = useState(false);
  const [streak, setStreak] = useState(0);
  const [celebrate, setCelebrate] = useState(null);
  const [bust, setBust] = useState(null);
  const [sessionLog, setSessionLog] = useState([]);
  const [recoveryBanner, setRecoveryBanner] = useState(null);
  const [score, setScore] = useState({ wins: 0, losses: 0 });

  // Which option the player chose last (left/right choice from the game config)
  const [lastChoice, setLastChoice] = useState(game.left.pick);
  const [muted, setMutedState] = useState(_muted);
  useEffect(() => {
    const sync = () => setMutedState(isMuted());
    window.addEventListener("naevo-fx-change", sync);
    return () => window.removeEventListener("naevo-fx-change", sync);
  }, []);

  const [baseStake, setBaseStake] = useState(() => Math.max(MIN_STAKE, Number(localStorage.getItem("naevo_arcade_stake")) || MIN_STAKE));
  const changeStake = (v) => { setBaseStake(v); localStorage.setItem("naevo_arcade_stake", String(v)); };
  const [nextStake, setNextStake] = useState(null);
  // Max Bet Guard — warn when a single manual stake is above 25% of the live balance
  const balanceNow = Number(localStorage.getItem("naevo_balance")) || 0;
  const maxSafeStake = balanceNow > 0 ? Number((balanceNow * GUARD_PCT).toFixed(2)) : null;
  const [smartStake, setSmartStake] = useState(() => localStorage.getItem("naevo_smart_stake") === "1");
  const toggleSmart = (on) => { setSmartStake(on); localStorage.setItem("naevo_smart_stake", on ? "1" : "0"); };
  const smartBase = safeBaseFor(balanceNow);
  useEffect(() => { if (smartStake && Math.abs(smartBase - baseStake) > 0.001) changeStake(smartBase); }, [smartStake, smartBase]); // eslint-disable-line react-hooks/exhaustive-deps
  const guardAckRef = useRef(0);
  const overGuard = (st) => maxSafeStake !== null && st > maxSafeStake;

  const abortRef = useRef(false);

  // ----- 30 plays / hour / game rate limit (prevents long losing grinds) -----
  const RATE_LIMIT_PER_HOUR = 30;
  const RATE_LIMIT_KEY = `naevo_arcade_runs_${game.key}`;
  const [runsThisHour, setRunsThisHour] = useState(0);
  const [cooldownMinLeft, setCooldownMinLeft] = useState(0);

  const _readRuns = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(RATE_LIMIT_KEY) || "[]");
      const cutoff = Date.now() - 60 * 60 * 1000;
      return raw.filter((t) => typeof t === "number" && t > cutoff);
    } catch { return []; }
  };
  const refreshRuns = () => {
    const runs = _readRuns();
    setRunsThisHour(runs.length);
    if (runs.length >= RATE_LIMIT_PER_HOUR) {
      const oldest = Math.min(...runs);
      const msLeft = Math.max(0, 60 * 60 * 1000 - (Date.now() - oldest));
      setCooldownMinLeft(Math.ceil(msLeft / 60000));
    } else {
      setCooldownMinLeft(0);
    }
    // persist filtered list so the array doesn't grow forever
    try { localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(runs)); } catch {}
    return runs;
  };
  // keep the counter ticking every 20s while the modal is open
  useEffect(() => {
    refreshRuns();
    const t = setInterval(refreshRuns, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.key]);
  // returns true if a new play is allowed; records the timestamp if so
  const consumeRun = () => {
    const runs = _readRuns();
    if (runs.length >= RATE_LIMIT_PER_HOUR) {
      const oldest = Math.min(...runs);
      const minLeft = Math.ceil(Math.max(0, 60 * 60 * 1000 - (Date.now() - oldest)) / 60000);
      setRunsThisHour(runs.length);
      setCooldownMinLeft(minLeft);
      toast.error(`${game.title} cooldown · 30/hr limit · try in ${minLeft}min`);
      return false;
    }
    runs.push(Date.now());
    try { localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(runs)); } catch {}
    setRunsThisHour(runs.length);
    return true;
  };
  const refundRun = () => {
    const runs = _readRuns();
    runs.pop();
    try { localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(runs)); } catch {}
    setRunsThisHour(runs.length);
  };
  const requireLogin = () => {
    if (!tradeAuth()) {
      toast.error("Login with Deriv to play", { id: "login-to-play", action: { label: "Login", onClick: () => connectToDeriv() } });
      return false;
    }
    const mins = Number(localStorage.getItem("naevo_session_minutes")) || 0;
    if (!sessionStorage.getItem("naevo_session_start")) sessionStorage.setItem("naevo_session_start", String(Date.now()));
    const elapsed = (Date.now() - Number(sessionStorage.getItem("naevo_session_start"))) / 60000;
    if (mins > 0 && elapsed >= mins) {
      toast.warning(`Session time limit (${mins} min) reached — take a break and come back later`, { id: "session-limit" });
      return false;
    }
    return true;
  };
  const limitsStop = async () => {
    const auth = tradeAuth();
    try {
      const r = await api.get("/player/limits", { params: { session_id: auth.session_id, account_id: auth.account_id } });
      if (r.data.locked) { toast.error(r.data.locked); return true; }
      if (r.data.target_hit) { toast.success(`Daily profit target hit (+$${r.data.today.pnl.toFixed(2)}) — auto-play stopped`); return true; }
    } catch {}
    return false;
  };

  // Fetch signal every 4s while open — pass the game's contract family
  useEffect(() => {
    let mounted = true;
    let timer;
    const fam = game.family || "digits";
    const pull = async () => {
      try {
        const r = await api.get(`/arcade/signal?family=${fam}`);
        if (mounted) {
          setSignal(r.data);
          setSignalLoading(false);
          // cache the locked market's last-digits so each scene can show the LIVE scoreboard
          try {
            const digits = r.data?.locked?.last_digits || [];
            localStorage.setItem("naevo_last_digits", JSON.stringify(digits));
          } catch {}
        }
      } catch (e) {
        if (mounted) setSignalLoading(false);
      }
    };
    pull();
    timer = setInterval(pull, 4000);
    return () => { mounted = false; clearInterval(timer); };
  }, [game.family]);

  // Expose state to AI widget
  useEffect(() => {
    try {
      localStorage.setItem("naevo_streak", String(streak));
      if (signal?.locked) localStorage.setItem("naevo_top_pair", `${signal.locked.label} · ${signal.locked.side}`);
    } catch {}
  }, [streak, signal]);

  // One win clears the hole + 5%, then back to base stake.
  const recoveryNextStake = recoveryStake;

  const winLabel = { penalty: "GOOOAL!", flip: "YOU WIN!", hoop: "SWISH!", treasure: "GOLD!", dice: "LUCKY!", wheel: "JACKPOT!" }[game.key];
  const lossLabel = { penalty: "SAVED!", flip: "WRONG!", hoop: "BRICK!", treasure: "EMPTY!", dice: "DUST!", wheel: "MISS!" }[game.key];

  // Place one round through the backend trade endpoint (real if session, sim otherwise)
  // Real Deriv contract on the account the user picked (Real or Demo). Returns null if it didn't settle.
  const singleRound = async (currentStake, side, symbol) => {
    const auth = tradeAuth();
    if (!auth) {
      toast.error("Login with Deriv to play", { action: { label: "Login", onClick: () => connectToDeriv() } });
      return null;
    }
    const contract_family = game.family || "digits";
    const client_ref = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
    const body = { stake: currentStake, side, symbol, contract_family, game: game.title, client_ref, session_id: auth.session_id, account_id: auth.account_id };
    const transient = (e) => !e?.response || e.response.status >= 500 || e.response.status === 429;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    let data = null;
    // Same client_ref on every retry → the server never buys twice.
    for (let attempt = 0; attempt < 4 && !data; attempt++) {
      try {
        data = (await api.post("/arcade/trade", body, { timeout: 30000 })).data;
      } catch (e) {
        if (!transient(e) || attempt === 3) { toastApiError(toast, e, "Trade failed — nothing was placed"); return null; }
        await sleep(1200 * (attempt + 1));
      }
    }
    // Poll until Deriv settles the contract (1-tick contracts usually settle in ~2s).
    for (let i = 0; i < 40 && data.status === "pending"; i++) {
      await sleep(1000);
      try { data = (await api.get(`/arcade/result/${data.contract_id}`, { params: { session_id: auth.session_id }, timeout: 15000 })).data; } catch {}
    }
    if (data.status !== "settled") {
      toast("Contract placed — result still settling, check History", { description: `#${data.contract_id}` });
      return null;
    }
    return {
      won: !!data.won,
      profit: Number(data.profit),
      mode: data.account_type,
      detail: { pick: lastChoice, side, symbol, family: contract_family },
    };
  };

  const applyRound = (res, prevHole, newHole) => {
    setResult(res);
    setAnimState("result");
    setScore((s) => ({ wins: s.wins + (res.won ? 1 : 0), losses: s.losses + (res.won ? 0 : 1) }));
    // win / loss stingers
    playSfx(res.won ? "win" : "loss");
    // per-game result sfx
    if (res.won) {
      if (game.key === "hoop") setTimeout(() => playSfx("swish"), 60);
      if (game.key === "treasure") setTimeout(() => playSfx("chest"), 60);
    }
    if (res.won) {
      const big = streak + 1 >= 3 || res.profit >= 4.75 || prevHole > 0.01;
      setStreak((s) => s + 1);
      setCelebrate({ amount: res.profit, label: big ? "JACKPOT!" : winLabel, big, stake: Number((res.profit / WIN_RATIO).toFixed(2)), game: game.title });
      setTimeout(() => playSfx(big ? "jackpot" : "coins"), 150);
      buzz(big ? BUZZ.jackpot : BUZZ.win);
      setTimeout(() => setCelebrate(null), big ? 3200 : 2200);
      if (prevHole > 0.01) {
        setRecoveryBanner(`Recovered + 5% · back to base stake $${baseStake.toFixed(2)}`);
        setTimeout(() => setRecoveryBanner(null), 2400);
      }
    } else {
      setStreak(0);
      // show a loss stinger with the exact amount lost
      setBust({ amount: Math.abs(res.profit), label: lossLabel });
      setTimeout(() => setBust(null), 1100);
    }
    // append to session log (last 12)
    setSessionLog((xs) => [{
      ts: Date.now(),
      won: res.won,
      profit: res.profit,
      stake: res.won ? Number((res.profit / WIN_RATIO).toFixed(2)) : Math.abs(res.profit),
    }, ...xs].slice(0, 12));
  };

  // One tap = one round. The pick (left/right) decides the family-specific side.
  const playOne = async (sideKey /* "left" | "right" */) => {
    if (playing || autoRunning) return;
    if (!requireLogin()) return;
    if (overGuard(baseStake) && guardAckRef.current !== baseStake) {
      toast.warning(`Max Bet Guard: $${baseStake.toFixed(2)} is over 25% of your $${balanceNow.toFixed(2)} balance`, {
        description: `Safer stake: $${maxSafeStake.toFixed(2)} or less.`,
        action: { label: "Play anyway", onClick: () => { guardAckRef.current = baseStake; } },
        duration: 6000,
      });
      return;
    }
    if (!consumeRun()) return; // 30/hr cap
    const opt = game[sideKey];
    setLastChoice(opt.pick);
    setPlaying(true);
    setResult(null);
    // visually reset the scene (keeper stands, striker returns) before the next kick
    setAnimState("idle");
    await new Promise((r) => setTimeout(r, 220));
    try {
      const symbol = signal?.locked?.symbol || "R_50";
      // Resolve the trade first so the scene can animate the KEEPER correctly on the way
      const res = await singleRound(baseStake, opt.side, symbol);
      if (!res) { refundRun(); setAnimState("idle"); return; }
      // launch the animation with the result already known
      playSfx("whistle");
      setResult(res);
      setAnimState("playing");
      if (game.key === "penalty") setTimeout(() => playSfx("kick"), 650);
      if (game.key === "flip")    playSfx("flip");
      if (game.key === "hoop")    setTimeout(() => playSfx("kick"), 250);
      if (game.key === "treasure") playSfx("flip");
      await new Promise((r) => setTimeout(r, 1100));
      applyRound(res, 0, 0);
      if (res.won) toast.success(`+$${res.profit.toFixed(2)}`);
      else toast.error(`-$${baseStake.toFixed(2)}`);
    } finally {
      setPlaying(false);
    }
  };

  // AUTO burst — always picks the LOCKED side from the signal engine
  const runAuto = async (total) => {
    if (!requireLogin()) { setAutoMode("off"); return; }
    const runsLeft = RATE_LIMIT_PER_HOUR - _readRuns().length;
    if (runsLeft < total) toast.warning(`Only ${runsLeft} of your 30 plays left this hour — auto ×${total} will stop after ${runsLeft}`);
    if (balanceNow > 0) {
      const survive = survivableLosses(baseStake, balanceNow);
      if (survive < TARGET_LOSS_RUN) {
        toast.warning(`Your balance covers ${survive} losses in a row at $${baseStake.toFixed(2)} — for ${TARGET_LOSS_RUN} use a base stake of $${safeBaseFor(balanceNow).toFixed(2)} or less`, { duration: 7000 });
      }
    }
    setAutoRunning(true);
    abortRef.current = false;
    setAutoProgress({ played: 0, total, pnl: 0 });
    let played = 0, pnl = 0, hole = 0;
    while (played < total) {
      if (abortRef.current) break;
      const locked = signal?.locked;
      if (!locked) { await new Promise((r) => setTimeout(r, 500)); continue; }
      // Enforce 30/hr cap mid-burst — stop when exhausted
      if (!consumeRun()) { abortRef.current = true; break; }
      const matchedKey = game.left.side === locked.side ? "left" : "right";
      const opt = game[matchedKey];
      setLastChoice(opt.pick);

      // Auto-play never stops on the guard — it plays every round so one win clears the losing run.
      const currentStake = recoveryNextStake(baseStake, hole);
      setNextStake(currentStake);

      // visually reset the scene (keeper stands, striker returns) between kicks
      setAnimState("idle");
      setResult(null);
      await new Promise((r) => setTimeout(r, 260));
      if (abortRef.current) break;

      // pre-resolve so the scene can orchestrate the dive correctly
      const res = await singleRound(currentStake, opt.side, locked.symbol);
      if (!res) { refundRun(); abortRef.current = true; setAnimState("idle"); break; }
      playSfx("whistle");
      setResult(res);
      setAnimState("playing");
      if (game.key === "penalty") setTimeout(() => playSfx("kick"), 500);
      if (game.key === "flip")    playSfx("flip");
      if (game.key === "hoop")    setTimeout(() => playSfx("kick"), 180);
      if (game.key === "treasure") playSfx("flip");
      await new Promise((r) => setTimeout(r, 820));
      if (abortRef.current) break;

      pnl = Number((pnl + res.profit).toFixed(2));
      const holeBefore = hole;
      if (res.won) {
        hole = 0;
      } else {
        hole = Number((hole + currentStake).toFixed(2));
      }
      played += 1;
      applyRound(res, holeBefore, hole);
      setNextStake(recoveryNextStake(baseStake, hole));
      setAutoProgress({ played, total, pnl });
      if (hole <= 0.01 && await limitsStop()) { abortRef.current = true; break; }
      if (!requireLogin()) { abortRef.current = true; break; }
      await new Promise((r) => setTimeout(r, 300));
    }
    setAutoRunning(false);
    setAutoMode("off");
    setNextStake(null);
    const finalPnl = pnl;
    if (played === 0) { toast("Auto-play stopped · no rounds played"); return; }
    toast[finalPnl >= 0 ? "success" : "error"](
      abortRef.current
        ? `Stopped at ${played} rounds · ${finalPnl >= 0 ? "+" : ""}$${finalPnl.toFixed(2)}`
        : `Auto ×${total} finished · ${finalPnl >= 0 ? "+" : ""}$${finalPnl.toFixed(2)}`
    );
  };

  const triggerAuto = (n) => {
    if (autoRunning) return;
    setAutoMode(String(n));
    runAuto(n);
  };

  const stopAuto = () => {
    abortRef.current = true;
  };
  useEffect(() => () => { abortRef.current = true; }, []);

  const locked = signal?.locked;
  const leftIsLocked = locked && game.left.side === locked.side;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md overflow-y-auto overscroll-contain"
      style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
      onClick={onClose}
      data-testid="arcade-modal-scroller"
    >
      <div className="min-h-full flex items-start justify-center px-3 sm:px-4 pt-[max(env(safe-area-inset-top),12px)] pb-24">
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        className="relative max-w-3xl w-full casino-frame rounded-3xl overflow-hidden my-3 sm:my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="arcade-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div>
            <AccountChip />
            <div className="font-display text-2xl">{game.title}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { const next = !muted; setMuted(next); setMutedState(next); }}
              data-testid="arcade-mute"
              className="h-9 w-9 rounded-full bg-white/5 border border-white/10 text-white/70 hover:text-white flex items-center justify-center"
              title={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <button
              className="h-9 w-9 rounded-full bg-white/5 border border-white/10 text-white/70 hover:text-white"
              onClick={onClose}
              data-testid="arcade-close"
            >×</button>
          </div>
        </div>

        {/* ---- GAME SCENE ---- */}
        <div className="relative h-[340px] overflow-hidden">
          {game.key === "penalty"  && <PenaltyScene  state={animState} choice={lastChoice} result={result} />}
          {game.key === "flip"     && <FlipScene     state={animState} choice={lastChoice} result={result} />}
          {game.key === "hoop"     && <HoopScene     state={animState} choice={lastChoice} result={result} />}
          {game.key === "treasure" && <TreasureScene state={animState} choice={lastChoice} result={result} />}
          {game.key === "dice"     && <DiceScene     state={animState} choice={lastChoice} result={result} />}
          {game.key === "wheel"    && <WheelScene    state={animState} choice={lastChoice} result={result} />}

          {streak >= 2 && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full border border-[#F5A623]/60 bg-[#F5A623]/15 px-3 py-1 text-xs font-mono-naevo text-[#F5A623]"
              data-testid="streak-badge"
            >
              🔥 {streak} IN A ROW
            </motion.div>
          )}

          <AnimatePresence>
            {celebrate && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(0,255,157,0.15), transparent 70%)" }}
              >
                <Confetti />
                <CoinShower count={celebrate.big ? 46 : 16} />
                <div className="text-center relative">
                  <motion.div
                    initial={{ scale: 0.4, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }}
                    transition={{ type: "spring", damping: 10 }}
                    className={`font-bold ${celebrate.big ? "font-casino text-gold-grad" : "font-display text-[color:var(--naevo-success)]"}`}
                    style={{ fontSize: celebrate.big ? "64px" : "72px", lineHeight: 1, textShadow: celebrate.big ? "none" : "0 0 40px rgba(0,255,157,0.7)" }}
                    data-testid={celebrate.big ? "jackpot-banner" : "win-banner"}
                  >
                    {celebrate.label}
                  </motion.div>
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }}
                    className="font-mono-naevo text-3xl text-[color:var(--naevo-success)] mt-3"
                  >
                    +${celebrate.amount.toFixed(2)}
                  </motion.div>
                  <motion.button
                    initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.5 }}
                    onClick={() => shareWin({ amount: celebrate.amount, stake: celebrate.stake, game: celebrate.game, label: celebrate.label })}
                    data-testid="share-win"
                    className="mt-5 pointer-events-auto rounded-full px-5 py-2 bg-magenta text-white font-mono-naevo text-sm hover:brightness-110 inline-flex items-center gap-2"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                    SHARE THE FLEX
                  </motion.button>
                </div>
              </motion.div>
            )}
            {bust && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1, x: [0, -10, 10, -6, 6, 0] }} exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(255,51,102,0.22), transparent 70%)" }}
              >
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0.4, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }}
                    transition={{ type: "spring", damping: 10 }}
                    className="font-display font-bold text-[color:var(--naevo-danger)]"
                    style={{ fontSize: "68px", lineHeight: 1, textShadow: "0 0 40px rgba(255,51,102,0.6)" }}
                  >
                    {bust.label}
                  </motion.div>
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.15 }}
                    className="font-mono-naevo text-3xl text-[color:var(--naevo-danger)] mt-3"
                  >
                    -${bust.amount.toFixed(2)}
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {recoveryBanner && (
            <motion.div
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="mx-6 mt-4 rounded-xl border border-[#F5A623]/50 bg-[#F5A623]/10 px-4 py-3 flex items-center gap-3"
              data-testid="recovery-banner"
            >
              <span className="text-xl">💰</span>
              <div className="text-sm text-[#F5D28A]">{recoveryBanner}</div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ---- CONTROLS ---- */}
        <div className="p-6 space-y-4">

          <StakeSelector stake={baseStake} onChange={changeStake} disabled={playing || autoRunning} smart={smartStake} onSmartToggle={toggleSmart} smartBase={smartBase} maxSafe={maxSafeStake} nextStake={autoRunning ? nextStake : null}
            balance={balanceNow} survive={balanceNow > 0 ? survivableLosses(baseStake, balanceNow) : null} target={TARGET_LOSS_RUN} safeBase={balanceNow > 0 ? safeBaseFor(balanceNow) : null} />

          {/* SIGNAL BANNER (yellow) */}
          <button
            disabled={!locked || playing || autoRunning}
            onClick={() => {
              if (!locked) return;
              const key = game.left.side === locked.side ? "left" : "right";
              playOne(key);
            }}
            data-testid="signal-banner"
            className="w-full rounded-xl border px-4 py-3 text-left transition-colors disabled:opacity-70"
            style={{
              borderColor: locked ? "#F5A623" : "rgba(255,255,255,0.1)",
              background: locked ? "linear-gradient(90deg, rgba(245,166,35,0.18), rgba(245,166,35,0.06))" : "rgba(255,255,255,0.03)",
            }}
          >
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#F5A623]" />
              <div className="font-mono-naevo text-sm text-[#F5D28A]">
                {signalLoading && !locked
                  ? "SCANNING MARKETS…"
                  : locked
                    ? <>{game.actionWord} THE SIGNAL — <span className="text-white">{leftIsLocked ? game.left.label.replace(/^SHOOT |^OPEN /,'') : game.right.label.replace(/^SHOOT |^OPEN /,'')}</span> · <span className="text-white">{locked.side}</span> · {locked.strength}/100</>
                    : "SIGNAL UNAVAILABLE"}
              </div>
            </div>
          </button>

          {/* TWO BIG BUTTONS */}
          <div className="grid grid-cols-2 gap-3">
            <GameButton
              side="left"
              game={game}
              signal={signal}
              onClick={() => playOne("left")}
              disabled={playing || autoRunning}
            />
            <GameButton
              side="right"
              game={game}
              signal={signal}
              onClick={() => playOne("right")}
              disabled={playing || autoRunning}
            />
          </div>

          {autoRunning && (
            <button
              onClick={() => { stopAuto(); toast("Stopping — the round in play will finish first"); }}
              data-testid="arcade-stop-big"
              className="w-full h-14 rounded-2xl btn-velvet text-base tracking-[0.2em] flex items-center justify-center gap-3"
            >
              <StopIcon className="h-4 w-4 fill-current" /> STOP AUTO-PLAY
              <span className="font-mono-naevo text-xs opacity-80">{autoProgress.played}/{autoProgress.total}</span>
            </button>
          )}

          {/* AUTO-KICK ROW */}
          <div className="flex items-center gap-2 flex-wrap pt-1" data-testid="auto-row">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 mr-1">
              <Zap className="h-3.5 w-3.5 text-magenta" /> AUTO-{game.actionWord === "SHOOT" ? "KICK" : game.actionWord}
            </div>
            {["off", "3", "5", "10", "30"].map((m) => {
              const isActive = autoMode === m;
              return (
                <button
                  key={m}
                  onClick={() => {
                    if (m === "off") { stopAuto(); setAutoMode("off"); }
                    else triggerAuto(Number(m));
                  }}
                  disabled={autoRunning && m !== "off"}
                  data-testid={`arcade-auto-${m}`}
                  className={`rounded-md px-3 py-1.5 text-xs font-mono-naevo border transition-colors ${
                    isActive ? "border-magenta bg-magenta/15 text-magenta" : "border-white/10 text-white/70 hover:border-white/30"
                  } ${autoRunning && m !== "off" ? "opacity-40" : ""}`}
                >
                  {m === "off" ? "OFF" : `×${m}`}
                </button>
              );
            })}
            {autoRunning && (
              <button
                onClick={() => { stopAuto(); toast("Stopping after this round…"); }}
                data-testid="arcade-stop"
                className="ml-1 rounded-md px-3 py-1.5 text-xs font-mono-naevo border border-[color:var(--naevo-danger)] bg-[color:var(--naevo-danger)]/15 text-[color:var(--naevo-danger)] hover:bg-[color:var(--naevo-danger)]/25 flex items-center gap-1.5"
              >
                <StopIcon className="h-3 w-3 fill-current" /> STOP
              </button>
            )}
            {autoRunning && (
              <div className="ml-auto text-xs font-mono-naevo">
                <span className="text-white/50">{autoProgress.played}/{autoProgress.total}</span>
                <span className={`ml-2 ${autoProgress.pnl >= 0 ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"}`}>
                  {autoProgress.pnl >= 0 ? "+" : ""}${autoProgress.pnl.toFixed(2)}
                </span>
              </div>
            )}
            {/* HOURLY CAP CHIP — always visible */}
            <div
              className={`${autoRunning ? "" : "ml-auto"} text-[11px] font-mono-naevo px-2.5 py-1 rounded-full border ${
                cooldownMinLeft > 0
                  ? "border-[color:var(--naevo-danger)]/60 bg-[color:var(--naevo-danger)]/10 text-[color:var(--naevo-danger)]"
                  : runsThisHour >= RATE_LIMIT_PER_HOUR - 5
                  ? "border-[#F5A623]/60 bg-[#F5A623]/10 text-[#F5A623]"
                  : "border-white/15 bg-white/5 text-white/60"
              }`}
              data-testid="hourly-cap"
              title="Max 30 plays per game per hour — prevents cold-streak drain"
            >
              {cooldownMinLeft > 0
                ? `COOLDOWN · retry in ${cooldownMinLeft}m`
                : `${runsThisHour}/${RATE_LIMIT_PER_HOUR} this hour`}
            </div>
          </div>

          {/* SIGNAL ENGINE PANEL */}
          <SignalEnginePanel signal={signal} loading={signalLoading} auto={autoMode !== "off"} family={game.family || "digits"} />

          {/* SCORE */}
          <div className="text-center text-sm text-white/60 font-mono-naevo pt-1" data-testid="score-row">
            {game.scoreL} <span className="text-[color:var(--naevo-success)] text-lg mx-1">{score.wins}</span>
            · {game.scoreR} <span className="text-[color:var(--naevo-danger)] text-lg mx-1">{score.losses}</span>
          </div>

          {/* SESSION LOG */}
          {sessionLog.length > 0 && (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 mt-2" data-testid="session-log">
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Session log</div>
              <div className="flex flex-wrap gap-1.5">
                {sessionLog.map((r, i) => (
                  <span
                    key={i}
                    data-testid={`session-row-${i}`}
                    className={`px-2 py-0.5 rounded font-mono-naevo text-[11px] border ${
                      r.won
                        ? "border-[color:var(--naevo-success)]/40 bg-[color:var(--naevo-success)]/10 text-[color:var(--naevo-success)]"
                        : "border-[color:var(--naevo-danger)]/40 bg-[color:var(--naevo-danger)]/10 text-[color:var(--naevo-danger)]"
                    }`}
                  >
                    {r.won ? "+" : "-"}${Math.abs(r.profit).toFixed(2)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*                        TWO BIG ACTION BUTTONS                      */
/* ------------------------------------------------------------------ */
function GameButton({ side, game, signal, onClick, disabled }) {
  const cfg = game[side];
  const locked = signal?.locked;
  const isLocked = locked && locked.side === cfg.side;
  // Which stat label to show per family
  const family = game.family || "digits";
  const sideShort = {
    EVEN: "E", ODD: "O",
    OVER: "H", UNDER: "L",
    RISE: "↑", FALL: "↓",
  }[cfg.side] || cfg.side[0];
  // signal.locked returns pct_a (A side) / pct_b (B side). But A/B order depends on family.
  // Easier: pull the matching pct from the top market by family.
  const famKeys = {
    digits:     { EVEN: "even_pct",  ODD: "odd_pct"   },
    over_under: { OVER: "over_pct",  UNDER: "under_pct" },
    rise_fall:  { RISE: "rise_pct",  FALL: "fall_pct"   },
  };
  const lockedMarket = signal?.markets?.find((m) => m.symbol === locked?.symbol);
  const pct = lockedMarket ? lockedMarket[famKeys[family]?.[cfg.side]] : null;
  const recent = pct != null ? `recent ${sideShort} ${pct}%` : "";

  // red (left) uses a warm palette, green (right) uses a success palette
  const red = side === "left";
  const palette = red
    ? { border: "rgba(239,80,80,0.45)", bg: "rgba(239,80,80,0.10)", text: "#FF7A7A", glow: "rgba(239,80,80,0.35)" }
    : { border: "rgba(0,220,140,0.45)", bg: "rgba(0,220,140,0.08)",  text: "#4ADE9B", glow: "rgba(0,220,140,0.35)" };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={`arcade-pick-${cfg.pick}`}
      className="rounded-xl border px-4 py-3 text-left transition-all disabled:opacity-50 hover:brightness-110"
      style={{
        borderColor: palette.border,
        background: palette.bg,
        boxShadow: isLocked ? `0 0 24px ${palette.glow}` : "none",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-mono-naevo opacity-90" style={{ color: palette.text }}>
            {cfg.label} · {cfg.side}
          </div>
          <div className="text-[10px] text-white/50 mt-1 font-mono-naevo">{cfg.hint}</div>
        </div>
        {isLocked && <span className="text-sm">🔥</span>}
      </div>
      <div className="mt-2 text-[10px] text-white/50 font-mono-naevo">{recent}</div>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*                        SIGNAL ENGINE PANEL                         */
/* ------------------------------------------------------------------ */
function SignalEnginePanel({ signal, loading, auto, family = "digits" }) {
  // Family-aware labels for the summary line + per-market rows
  const famCfg = {
    digits:     { a: "even_pct",  b: "odd_pct",   label: "digits", aLabel: "even", bLabel: "odd",  cellA: "E", cellB: "O" },
    over_under: { a: "over_pct",  b: "under_pct", label: "ticks",  aLabel: "over 4", bLabel: "under 5", cellA: "H", cellB: "L" },
    rise_fall:  { a: "rise_pct",  b: "fall_pct",  label: "ticks",  aLabel: "rise",  bLabel: "fall", cellA: "↑", cellB: "↓" },
  }[family] || { a: "even_pct", b: "odd_pct", label: "digits", aLabel: "even", bLabel: "odd", cellA: "E", cellB: "O" };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="signal-engine">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#F5A623]">
          <Zap className="h-3.5 w-3.5" /> Signal engine
        </div>
        <div className={`text-[10px] font-mono-naevo px-2 py-1 rounded-full border ${auto ? "border-[color:var(--naevo-success)]/50 text-[color:var(--naevo-success)] bg-[color:var(--naevo-success)]/10" : "border-white/10 text-white/50"}`}>
          {auto ? "AUTO ON" : "AUTO OFF"}
        </div>
      </div>
      <div className="text-[11px] text-white/50 mt-1 font-mono-naevo">
        10 markets · last 50 ticks · auto-locks the strongest setup
      </div>

      {signal?.locked && (() => {
        const lockedMarket = signal.markets?.find((m) => m.symbol === signal.locked.symbol) || signal.locked;
        const aPct = lockedMarket[famCfg.a] ?? 50;
        const bPct = lockedMarket[famCfg.b] ?? 50;
        const dominant = aPct >= bPct ? famCfg.aLabel : famCfg.bLabel;
        return (
          <div className="mt-3 rounded-lg border border-[color:var(--naevo-success)]/40 bg-[color:var(--naevo-success)]/5 px-3 py-2">
            <div className="text-[11px] font-mono-naevo text-[color:var(--naevo-success)]">
              SETUP FOUND — <span className="text-white">{signal.locked.side}</span> on <span className="text-white">{signal.locked.label}</span> · strength <span className="text-white">{signal.locked.strength}/100</span>
            </div>
            <div className="text-[10px] text-white/50 mt-1">
              {Math.max(aPct, bPct)} of last 50 {famCfg.label} {dominant} · the next tick can still go either way.
            </div>
          </div>
        );
      })()}

      {signal?.markets && (
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          {signal.markets.map((m) => {
            const isLocked = signal.locked?.symbol === m.symbol;
            const aPct = m[famCfg.a] ?? 0;
            const bPct = m[famCfg.b] ?? 0;
            return (
              <div
                key={m.symbol}
                data-testid={`market-row-${m.symbol}`}
                className={`rounded-md border px-2.5 py-1.5 text-[11px] font-mono-naevo flex items-center justify-between ${
                  isLocked ? "border-[#F5A623]/60 bg-[#F5A623]/10 text-[#F5D28A]" : "border-white/10 text-white/60"
                }`}
              >
                <span>{isLocked && "🎯 "}{m.label}</span>
                <span className="text-white/70">{famCfg.cellA} {aPct}% / {famCfg.cellB} {bPct}%</span>
              </div>
            );
          })}
        </div>
      )}

      {!signal && loading && (
        <div className="mt-3 text-[11px] text-white/50 font-mono-naevo">Scanning 10 markets…</div>
      )}

      <div className="mt-3 text-[10px] text-white/35 flex items-center gap-1.5">
        <Shield className="h-3 w-3" /> Smart Recovery · Smart Stake running silently in the background
      </div>
    </div>
  );
}

/* ================================================================== */
/* ===================         SCENES          ===================== */
/* ================================================================== */

/* ---------- Penalty Shootout (vts-style with back-view striker + trash talk) ---------- */
const KEEPER_TRASH = [
  "my grandma dives better than that",
  "nothing's getting past me today",
  "see this? empty net energy",
  "aim with your eyes closed next time",
  "wake me up when you aim better",
  "I eat strikers like you for breakfast",
  "that was cute. try again.",
  "zzz … catch me if u can",
];
const STRIKER_TRASH = [
  "aiming for your weak side 👀",
  "say hi to the back of the net",
  "camera don't blink",
  "goal #1000 incoming",
  "this one's for the fans",
  "ur gonna fetch this one",
  "sit down keeper, sit down",
  "watch my feet, not my eyes",
];
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function PenaltyScene({ state, choice, result }) {
  const won = !!result?.won;
  // On WIN keeper dives OPPOSITE (ball sails past), on LOSS keeper dives SAME side (SAVE)
  const keeperDir =
    result == null ? 0 :
    won ? (choice === "left" ? 1 : -1)
        : (choice === "left" ? -1 : 1);
  const ballDir = choice === "left" ? -1 : 1;

  const [talk, setTalk] = useState({ k: pick(KEEPER_TRASH), s: null });
  useEffect(() => {
    if (state === "idle") setTalk({ k: pick(KEEPER_TRASH), s: null });
    if (state === "playing") setTalk((t) => ({ ...t, s: pick(STRIKER_TRASH) }));
    if (state === "result") {
      // winner trash-talks the loser
      if (won) setTalk({ s: pick(["GOALLL! stay down", "sent u into orbit", "4-0 keeper btw", "pack it up"]), k: null });
      else     setTalk({ k: pick(["told you. told you.", "SAVED. refund?", "that's a yoink", "stay in bronze"]), s: null });
    }
  }, [state, won]);

  const digits = (() => {
    try { return JSON.parse(localStorage.getItem("naevo_last_digits") || "[]"); } catch { return []; }
  })();

  // ---- STANDING keeper (idle) ----
  const KeeperStand = () => (
    <svg width="100" height="150" viewBox="0 0 100 150" style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id="ksDark" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#2a2a30" /><stop offset="1" stopColor="#0d0d12" />
        </linearGradient>
        <linearGradient id="ksSkin" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#e6c097" /><stop offset="1" stopColor="#b58862" />
        </linearGradient>
      </defs>
      <ellipse cx="50" cy="146" rx="20" ry="3" fill="rgba(0,0,0,0.5)" />
      {/* legs */}
      <path d="M 40 86 L 38 128 Q 38 140 44 142 L 52 142 Q 54 140 52 128 L 50 86 Z" fill="url(#ksDark)" />
      <path d="M 54 86 L 56 128 Q 56 140 62 142 L 70 142 Q 72 140 70 128 L 68 86 Z" fill="url(#ksDark)" />
      <ellipse cx="48" cy="144" rx="9" ry="3" fill="#111" />
      <ellipse cx="66" cy="144" rx="9" ry="3" fill="#111" />
      {/* torso */}
      <path d="M 30 42 Q 34 36 44 34 L 58 34 Q 68 36 72 42 L 74 76 Q 66 80 50 80 Q 34 80 28 76 Z" fill="url(#ksDark)" />
      <text x="50" y="60" fontSize="12" fontWeight="900" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontFamily="Impact, Arial Black">1</text>
      {/* arms slightly out */}
      <motion.g animate={state === "idle" ? { rotate: [0, 2, -2, 0] } : {}} transition={{ duration: 2, repeat: Infinity }} style={{ transformOrigin: "30px 44px" }}>
        <path d="M 30 42 Q 20 52 16 70 L 22 72 Q 26 60 32 50 Z" fill="url(#ksDark)" />
        <ellipse cx="19" cy="74" rx="5" ry="4" fill="#F5F0E0" stroke="#8A7520" strokeWidth="1" />
      </motion.g>
      <motion.g animate={state === "idle" ? { rotate: [0, -2, 2, 0] } : {}} transition={{ duration: 2, repeat: Infinity }} style={{ transformOrigin: "72px 44px" }}>
        <path d="M 72 42 Q 82 52 86 70 L 80 72 Q 76 60 70 50 Z" fill="url(#ksDark)" />
        <ellipse cx="83" cy="74" rx="5" ry="4" fill="#F5F0E0" stroke="#8A7520" strokeWidth="1" />
      </motion.g>
      {/* head */}
      <ellipse cx="50" cy="22" rx="11" ry="12" fill="url(#ksSkin)" />
      <path d="M 40 18 Q 42 8 50 8 Q 58 8 60 18 Q 58 14 54 12 Q 46 12 40 18 Z" fill="#1a1308" />
      <circle cx="47" cy="22" r="1.2" fill="#111" />
      <circle cx="53" cy="22" r="1.2" fill="#111" />
      <path d="M 46 28 Q 50 29 54 28" stroke="#111" strokeWidth="0.8" fill="none" />
    </svg>
  );

  // ---- DIVING keeper (shown only during playing/result) ----
  const KeeperDive = ({ dir = 1 }) => (
    <svg width="150" height="110" viewBox="0 0 150 110" style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id="kdDark" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#2a2a30" /><stop offset="1" stopColor="#0d0d12" />
        </linearGradient>
      </defs>
      <ellipse cx="75" cy="100" rx="55" ry="4" fill="rgba(0,0,0,0.45)" />
      <g transform={`scale(${dir},1) translate(${dir < 0 ? -150 : 0},0)`}>
        <path d="M 50 54 Q 36 60 24 68 Q 18 72 20 76 L 28 78 Q 36 72 44 68 Q 54 62 60 58 Z" fill="url(#kdDark)" />
        <path d="M 90 54 Q 108 56 126 60 Q 134 62 134 66 L 128 70 Q 118 68 108 66 Q 94 62 86 58 Z" fill="url(#kdDark)" />
        <ellipse cx="22" cy="76" rx="7" ry="3" fill="#111" />
        <ellipse cx="132" cy="66" rx="7" ry="3" fill="#111" />
        <path d="M 48 44 Q 54 36 68 36 L 88 36 Q 100 38 104 46 L 100 60 Q 86 64 70 64 Q 54 64 50 58 Z" fill="url(#kdDark)" />
        <text x="75" y="54" fontSize="8" fontWeight="900" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontFamily="Impact, Arial Black">1</text>
        <path d="M 50 44 Q 42 50 36 60 Q 32 66 30 72 L 36 74 Q 40 68 46 60 Q 52 52 56 46 Z" fill="url(#kdDark)" />
        <ellipse cx="33" cy="72" rx="6" ry="5" fill="#F5F0E0" stroke="#8A7520" strokeWidth="1" />
        <path d="M 100 44 Q 112 44 124 46 Q 136 48 144 52 L 142 56 Q 130 54 118 52 Q 108 50 102 48 Z" fill="url(#kdDark)" />
        <ellipse cx="146" cy="54" rx="7" ry="5" fill="#F5F0E0" stroke="#8A7520" strokeWidth="1" />
        <ellipse cx="40" cy="42" rx="8" ry="9" fill="#e6c097" />
      </g>
    </svg>
  );

  // ---- BACK-VIEW striker (we see his back + jersey number since he's facing the goal) ----
  const StrikerBack = ({ kicking = false, dir = "left" }) => {
    const kickDir = dir === "left" ? -1 : 1;
    return (
      <svg width="100" height="150" viewBox="0 0 100 150" style={{ overflow: "visible" }}>
        <defs>
          <linearGradient id="sbKit" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#1f2a55" />
            <stop offset="0.5" stopColor="#152040" />
            <stop offset="1" stopColor="#0a1328" />
          </linearGradient>
          <linearGradient id="sbSkin" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#eac29a" />
            <stop offset="1" stopColor="#b88d6a" />
          </linearGradient>
        </defs>
        <ellipse cx="50" cy="146" rx="20" ry="3" fill="rgba(0,0,0,0.45)" />
        {/* planted leg */}
        <path d="M 42 86 L 40 128 Q 40 140 46 142 L 54 142 Q 56 140 54 128 L 52 86 Z" fill="url(#sbKit)" />
        <ellipse cx="50" cy="144" rx="9" ry="3" fill="#111" />
        {/* kicking leg — swings up/sideways */}
        <motion.g
          animate={kicking ? { rotate: kickDir * -72 } : { rotate: 0 }}
          transition={{ duration: 0.26, delay: 0.3 }}
          style={{ transformOrigin: "56px 86px" }}
        >
          <path d="M 54 86 L 56 128 Q 56 140 62 142 L 70 142 Q 72 140 70 128 L 68 86 Z" fill="url(#sbKit)" />
          <ellipse cx="66" cy="144" rx="10" ry="3" fill="#FFF" />
        </motion.g>
        {/* shorts */}
        <path d="M 34 70 L 70 70 L 72 92 Q 64 94 50 94 Q 36 94 32 92 Z" fill="url(#sbKit)" />
        {/* torso — BACK view, flatter shoulders, bigger number */}
        <path d="M 28 40 Q 32 32 42 30 L 60 30 Q 70 32 74 40 L 76 72 Q 64 78 50 78 Q 36 78 26 72 Z" fill="url(#sbKit)" />
        {/* jersey number on BACK — larger, legible */}
        <text x="50" y="60" fontSize="22" fontWeight="900" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontFamily="Impact, Arial Black">9</text>
        {/* nameplate strip */}
        <text x="50" y="38" fontSize="5" fontWeight="700" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontFamily="IBM Plex Mono">NAEVO</text>
        {/* back arm */}
        <motion.g
          animate={kicking ? { rotate: kickDir * 34 } : { rotate: 0 }}
          transition={{ duration: 0.26, delay: 0.28 }}
          style={{ transformOrigin: "28px 42px" }}
        >
          <path d="M 28 40 Q 20 48 16 62 Q 12 72 14 80 L 20 80 Q 22 70 26 60 Q 30 50 34 44 Z" fill="url(#sbKit)" />
          <circle cx="17" cy="80" r="5" fill="url(#sbSkin)" />
        </motion.g>
        {/* front arm */}
        <motion.g
          animate={kicking ? { rotate: kickDir * -30 } : { rotate: 0 }}
          transition={{ duration: 0.26, delay: 0.28 }}
          style={{ transformOrigin: "74px 42px" }}
        >
          <path d="M 74 40 Q 82 48 86 62 Q 90 72 88 80 L 82 80 Q 80 70 76 60 Q 72 50 68 44 Z" fill="url(#sbKit)" />
          <circle cx="85" cy="80" r="5" fill="url(#sbSkin)" />
        </motion.g>
        {/* HEAD — back of the head (dark hair cap, no face) */}
        <ellipse cx="50" cy="22" rx="11" ry="12" fill="#2a1708" />
        <path d="M 40 24 Q 42 12 50 12 Q 58 12 60 24 L 60 32 Q 50 34 40 32 Z" fill="#1a0f05" />
        {/* small ear hints */}
        <ellipse cx="40" cy="24" rx="1.5" ry="2.5" fill="url(#sbSkin)" />
        <ellipse cx="60" cy="24" rx="1.5" ry="2.5" fill="url(#sbSkin)" />
        {/* neck */}
        <rect x="46" y="32" width="8" height="4" fill="url(#sbSkin)" />
      </svg>
    );
  };

  // trash-talk speech bubble
  const Bubble = ({ side, text, color }) => (
    <AnimatePresence>
      {text && (
        <motion.div
          initial={{ opacity: 0, y: 6, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6 }}
          className={`absolute z-20 px-2.5 py-1 rounded-xl text-[11px] font-mono-naevo max-w-[160px] text-center ${
            color === "pink" ? "bg-magenta/90 text-white" : "bg-white/90 text-black"
          }`}
          style={{
            [side === "left" ? "left" : "right"]: 14,
            top: side === "keeper" ? 110 : "auto",
            bottom: side === "striker" ? 110 : "auto",
            borderBottomLeftRadius: side === "keeper" && side === "left" ? 0 : undefined,
          }}
        >
          "{text}"
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: "linear-gradient(180deg, #050410 0%, #0a0820 100%)" }}>
      <div className="absolute left-0 right-0 top-0 h-8 opacity-50" style={{ background: "repeating-linear-gradient(90deg, rgba(255,255,255,0.1) 0 2px, transparent 2px 5px), linear-gradient(180deg, rgba(10,20,40,0.9), transparent)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 60% at 25% 0%, rgba(255,245,200,0.14), transparent 60%), radial-gradient(ellipse 40% 60% at 75% 0%, rgba(255,245,200,0.14), transparent 60%)" }} />

      {digits.length > 0 && (
        <div className="absolute top-1 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 px-3 py-1 rounded-md bg-black/60 border border-white/10">
          <span className="text-[10px] font-mono-naevo tracking-widest text-[color:var(--naevo-success)]">LIVE</span>
          {digits.map((d, i) => {
            const isLast = i === digits.length - 1;
            const isEven = d % 2 === 0;
            return (
              <span key={i} className={`text-[11px] font-mono-naevo w-5 text-center ${isLast ? "text-[#F5A623] border border-[#F5A623]/60 rounded-sm bg-[#F5A623]/10" : isEven ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"}`}>{d}</span>
            );
          })}
        </div>
      )}

      {/* perspective pitch */}
      <svg viewBox="0 0 600 380" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="pitchFloor" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#0a1428" /><stop offset="1" stopColor="#06091a" />
          </linearGradient>
        </defs>
        <rect y="140" width="600" height="240" fill="url(#pitchFloor)" />
        {[-5,-4,-3,-2,-1,0,1,2,3,4,5].map((i) => {
          const topX = 300 + i * 20;
          const bottomX = 300 + i * 110;
          return <line key={`v${i}`} x1={topX} y1={140} x2={bottomX} y2={380} stroke="rgba(80,160,255,0.14)" strokeWidth="1" />;
        })}
        {[160, 200, 250, 310].map((y) => (
          <line key={`h${y}`} x1="0" y1={y} x2="600" y2={y} stroke="rgba(80,160,255,0.1)" strokeWidth="1" />
        ))}
      </svg>

      {/* goal frame */}
      <svg viewBox="0 0 600 380" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <defs>
          <pattern id="netMesh2" width="12" height="12" patternUnits="userSpaceOnUse">
            <path d="M 0 0 L 12 12 M 12 0 L 0 12" stroke="rgba(255,255,255,0.5)" strokeWidth="0.7" />
          </pattern>
        </defs>
        <rect x="180" y="48" width="240" height="110" fill="url(#netMesh2)" opacity="0.65" />
        <rect x="180" y="48" width="240" height="110" fill="none" stroke="#fff" strokeWidth="4" rx="1" />
        <rect x="177" y="46" width="7" height="115" fill="rgba(255,255,255,0.4)" />
        <rect x="416" y="46" width="7" height="115" fill="rgba(255,255,255,0.4)" />
        <circle cx="300" cy="270" r="4" fill="rgba(255,255,255,0.8)" />
      </svg>

      {/* KEEPER — stands by default, dives only once ball is kicked */}
      {state === "idle" ? (
        <motion.div
          animate={{ x: [-6, 6, -6] }} transition={{ duration: 2, repeat: Infinity }}
          className="absolute left-1/2 top-[36px]"
          style={{ translateX: "-50%" }}
        >
          <KeeperStand />
        </motion.div>
      ) : (
        <motion.div
          initial={{ x: 0, y: -40, rotate: 0 }}
          animate={{ x: keeperDir * 85, y: 20, rotate: keeperDir * 12 }}
          transition={{ duration: 0.42, delay: 0.5 }}
          className="absolute left-1/2 top-[70px]"
          style={{ translateX: "-50%" }}
        >
          <KeeperDive dir={keeperDir || 1} />
        </motion.div>
      )}

      {/* keeper trash-talk bubble (top-right of goal when idle, flipped on result) */}
      <Bubble side="keeper" text={talk.k} color="white" />

      {/* BALL — stays at striker's foot during idle, flies in parabolic arc to the goal on play */}
      <motion.div
        animate={
          state === "idle" ? { x: 0, y: 0, scale: 1, rotate: 0 } :
          state === "playing" ? { x: ballDir * 130, y: [-10, -160, -200], scale: [1, 0.6, 0.4], rotate: 1080 } :
          won ? { x: ballDir * 150, y: -220, scale: 0.35, rotate: 1440 } :
                { x: ballDir * 95, y: -130, scale: 0.5, rotate: 720 }
        }
        transition={state === "playing" ? { duration: 0.72, times: [0, 0.55, 1], ease: "easeOut", delay: 0.4 } : { duration: 0.5, ease: "easeOut" }}
        className="absolute left-1/2 bottom-[26px]"
        style={{ translateX: "-50%" }}
      >
        <svg width="26" height="26" viewBox="0 0 26 26">
          <defs>
            <radialGradient id="ballG2" cx="0.35" cy="0.35" r="0.7">
              <stop offset="0" stopColor="#fff" /><stop offset="0.5" stopColor="#F5A623" /><stop offset="1" stopColor="#9A5000" />
            </radialGradient>
          </defs>
          <circle cx="13" cy="13" r="11" fill="url(#ballG2)" stroke="#111" strokeWidth="1" />
          <polygon points="13,5 17,9 15,14 11,14 9,9" fill="#111" opacity="0.85" />
          <polygon points="6,12 10,10 12,15 8,17" fill="#111" opacity="0.85" />
          <polygon points="20,12 16,10 14,15 18,17" fill="#111" opacity="0.85" />
        </svg>
      </motion.div>

      {/* STRIKER — BACK view, positioned BEHIND the ball, kicks in place */}
      <motion.div
        animate={state === "idle" ? { rotate: 0 } : state === "playing" ? { rotate: [0, -2, 2, choice === "left" ? -4 : 4, 0] } : {}}
        transition={state === "idle" ? {} : { duration: 0.4, ease: "easeInOut" }}
        className="absolute left-1/2 bottom-[-18px]"
        style={{ translateX: "-50%" }}
      >
        <StrikerBack kicking={state !== "idle"} dir={choice} />
      </motion.div>

      {/* striker trash-talk bubble */}
      <Bubble side="striker" text={talk.s} color="pink" />

      <AnimatePresence>
        {state === "playing" && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 0.6, 0], scale: [0.3, 1.2, 1.8] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.55, delay: 0.38 }}
            className="absolute left-1/2 bottom-[14px] pointer-events-none"
            style={{ translateX: "-50%" }}
          >
            <svg width="60" height="30" viewBox="0 0 60 30">
              <ellipse cx="30" cy="20" rx="25" ry="7" fill="rgba(255,255,255,0.35)" />
              <ellipse cx="18" cy="18" rx="7" ry="4" fill="rgba(255,255,255,0.45)" />
              <ellipse cx="42" cy="18" rx="7" ry="4" fill="rgba(255,255,255,0.45)" />
            </svg>
          </motion.div>
        )}
      </AnimatePresence>

      {state === "result" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
          className={`absolute top-10 left-1/2 -translate-x-1/2 px-6 py-2 rounded-full font-display text-3xl ${won ? "bg-[color:var(--naevo-success)]/20 text-[color:var(--naevo-success)] border border-[color:var(--naevo-success)]/50" : "bg-[color:var(--naevo-danger)]/20 text-[color:var(--naevo-danger)] border border-[color:var(--naevo-danger)]/50"}`}
        >{won ? "GOAL!" : "SAVED!"}</motion.div>
      )}
    </div>
  );
}
function FlipScene({ state, choice, result }) {
  const actual = result?.detail?.pick || choice;
  const flipDeg = state === "playing" ? [0, 180, 360, 540, 720, 900] : state === "result" ? (actual === "red" ? 0 : 180) : 0;
  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden" style={{ background: "radial-gradient(ellipse at center, #1a0a1a 0%, #050305 70%)" }}>
      <svg viewBox="0 0 600 380" className="absolute inset-0 w-full h-full">
        <radialGradient id="felt" cx="0.5" cy="0.5" r="0.6">
          <stop offset="0" stopColor="rgba(232,181,74,0.1)" />
          <stop offset="1" stopColor="transparent" />
        </radialGradient>
        <rect width="600" height="380" fill="url(#felt)" />
      </svg>
      <div className="absolute left-10 top-1/2 -translate-y-1/2 flex flex-col gap-2">
        {[1, 2, 3].map((i) => (
          <motion.div key={i} animate={{ y: [0, -4, 0] }} transition={{ duration: 2, delay: i * 0.2, repeat: Infinity }} className="h-10 w-10 rounded-full border-4 border-[color:var(--naevo-danger)] bg-[color:var(--naevo-danger)]/30" />
        ))}
      </div>
      <div className="absolute right-10 top-1/2 -translate-y-1/2 flex flex-col gap-2">
        {[1, 2, 3].map((i) => (
          <motion.div key={i} animate={{ y: [0, -4, 0] }} transition={{ duration: 2, delay: i * 0.2, repeat: Infinity }} className="h-10 w-10 rounded-full border-4 border-white/50 bg-black/70" />
        ))}
      </div>
      <div style={{ perspective: 1200 }}>
        <motion.div
          animate={{ rotateY: flipDeg }}
          transition={{ duration: state === "playing" ? 1.4 : 0.4, ease: "easeInOut" }}
          style={{ transformStyle: "preserve-3d", width: 150, height: 220, position: "relative" }}
        >
          <div className="absolute inset-0 rounded-xl flex items-center justify-center text-white font-display text-6xl"
            style={{ backfaceVisibility: "hidden", background: "linear-gradient(135deg, #FF3366 0%, #B32044 100%)", border: "3px solid rgba(255,255,255,0.4)", boxShadow: "0 0 40px rgba(255,51,102,0.5)" }}>
            ♥
          </div>
          <div className="absolute inset-0 rounded-xl flex items-center justify-center text-white font-display text-6xl"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)", background: "linear-gradient(135deg, #1a1a1a 0%, #000 100%)", border: "3px solid rgba(255,255,255,0.4)", boxShadow: "0 0 40px rgba(0,0,0,0.9)" }}>
            ♠
          </div>
        </motion.div>
      </div>
      {state === "result" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className={`absolute top-6 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full font-display text-2xl ${result.won ? "bg-[color:var(--naevo-success)]/20 text-[color:var(--naevo-success)] border border-[color:var(--naevo-success)]/50" : "bg-[color:var(--naevo-danger)]/20 text-[color:var(--naevo-danger)] border border-[color:var(--naevo-danger)]/50"}`}
        >{result.won ? "WIN" : "LOSS"} · {actual.toUpperCase()}</motion.div>
      )}
    </div>
  );
}

/* ---------- Hoop Shot (realistic rebuild v3 — responsive ball path) ---------- */
function HoopScene({ state, choice, result }) {
  const won = !!result?.won;

  // Measure the scene so ball coords stay accurate on any screen size
  const sceneRef = useRef(null);
  const [dims, setDims] = useState({ w: 1100, h: 460 });
  useEffect(() => {
    const el = sceneRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) setDims({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Player's hand release point (bottom-left area) and rim center (middle, upper third)
  const START = { x: dims.w * 0.14, y: dims.h * 0.72 };
  const RIM   = { x: dims.w * 0.50, y: dims.h * 0.36 };
  const dx = RIM.x - START.x;
  const dy = RIM.y - START.y;

  // Parabolic flight: ball goes from START up over a peak then lands at the rim.
  const playFlight = {
    x: [0, dx * 0.5, dx],
    y: [0, dy - dims.h * 0.22, dy],  // peak ~22% of scene height above rim
    scale: [1, 0.78, 0.6],
    rotate: 900,
    opacity: 1,
  };
  // WIN: ball drops cleanly through the net and exits a bit below the rim
  const winDrop = {
    x: [dx, dx + 3, dx + 1, dx - 2],
    y: [dy, dy + dims.h * 0.05, dy + dims.h * 0.12, dy + dims.h * 0.19],
    scale: [0.6, 0.55, 0.5, 0.45],
    opacity: [1, 1, 1, 0.35],
    rotate: 1200,
  };
  // LOSS: ball clips the near rim and bounces outward, falling to the floor beside the hoop
  const lossBounce = {
    x: [dx, dx + dims.w * 0.05, dx + dims.w * 0.14, dx + dims.w * 0.22],
    y: [dy, dy - dims.h * 0.02, dy + dims.h * 0.18, dy + dims.h * 0.42],
    scale: [0.6, 0.62, 0.68, 0.72],
    rotate: 1440,
    opacity: 1,
  };
  const ballAnim =
    state === "idle" ? { x: 0, y: 0, scale: 1, rotate: 0, opacity: 1 } :
    state === "playing" ? playFlight :
    won ? winDrop : lossBounce;
  const ballTransition =
    state === "playing" ? { duration: 0.85, times: [0, 0.55, 1], ease: [0.33, 0, 0.3, 1], delay: 0.15 } :
    state === "result"  ? { duration: 0.7, ease: "easeIn", times: [0, 0.35, 0.7, 1] } :
                          { duration: 0.4 };

  // Hoop dimensions also scale with the scene
  const hoopW = Math.max(220, Math.min(360, dims.w * 0.28));
  const hoopH = hoopW * (260 / 300);

  return (
    <div
      ref={sceneRef}
      className="absolute inset-0 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #16082a 0%, #2a0f45 55%, #080418 100%)" }}
    >
      {/* arena top border + spotlight */}
      <div className="absolute left-0 right-0 top-0 h-10 opacity-40" style={{ background: "repeating-linear-gradient(90deg, rgba(255,255,255,0.08) 0 2px, transparent 2px 5px)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 45% at 50% 0%, rgba(255,220,120,0.22), transparent 60%)" }} />

      {/* crowd silhouettes */}
      <svg viewBox="0 0 1100 120" className="absolute left-0 right-0 top-6 w-full h-28 opacity-30" preserveAspectRatio="none">
        {[...Array(26)].map((_, i) => (
          <circle key={i} cx={25 + i * 42} cy={60 + (i % 3) * 10} r="14" fill="#1a0a28" />
        ))}
        {[...Array(26)].map((_, i) => (
          <rect key={`b${i}`} x={11 + i * 42} y={70 + (i % 3) * 10} width="28" height="40" fill="#120820" />
        ))}
      </svg>

      {/* polished floor with perspective */}
      <svg viewBox="0 0 1100 460" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="hFloor" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#C68E55" />
            <stop offset="1" stopColor="#3a1f0a" />
          </linearGradient>
          <linearGradient id="hFloorShine" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="rgba(255,220,180,0.25)" />
            <stop offset="1" stopColor="rgba(255,220,180,0)" />
          </linearGradient>
        </defs>
        <polygon points="0,460 1100,460 900,250 200,250" fill="url(#hFloor)" />
        <polygon points="0,460 1100,460 900,250 200,250" fill="url(#hFloorShine)" opacity="0.5" />
        {[-5,-4,-3,-2,-1,0,1,2,3,4,5].map((i) => {
          const topX = 550 + i * 44;
          const bottomX = 550 + i * 220;
          return <line key={i} x1={topX} y1={250} x2={bottomX} y2={460} stroke="rgba(255,255,255,0.12)" strokeWidth="1" />;
        })}
        {[280, 340, 420].map((y) => (
          <line key={y} x1={200 + (460 - y) * 1.5} y1={y} x2={900 - (460 - y) * 1.5} y2={y} stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
        ))}
        <path d="M 300 380 Q 550 290 800 380" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />
        <rect x="470" y="260" width="160" height="130" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      </svg>

      {/* ---- HOOP ASSEMBLY — centered at RIM.x; scales with scene ---- */}
      <svg
        viewBox="0 0 300 260"
        width={hoopW}
        height={hoopH}
        className="absolute z-10"
        style={{
          left: RIM.x - hoopW / 2,
          top: RIM.y - hoopH * (158 / 260),  // rim-y lives at 158/260 of the SVG viewBox
        }}
      >
        <defs>
          <linearGradient id="rimG2" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#FF8A3C" />
            <stop offset="0.5" stopColor="#E25010" />
            <stop offset="1" stopColor="#8A2A05" />
          </linearGradient>
          <linearGradient id="poleG2" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#777" />
            <stop offset="0.5" stopColor="#aaa" />
            <stop offset="1" stopColor="#555" />
          </linearGradient>
          <linearGradient id="boardG2" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="rgba(255,255,255,0.12)" />
            <stop offset="1" stopColor="rgba(255,255,255,0.04)" />
          </linearGradient>
        </defs>

        {/* support arm */}
        <rect x="146" y="18" width="8" height="36" fill="url(#poleG2)" />

        {/* BACKBOARD */}
        <motion.g
          animate={!won && state === "result" ? { x: [0, 4, -4, 2, -2, 0] } : {}}
          transition={{ duration: 0.35 }}
        >
          <rect x="40" y="20" width="220" height="130" rx="4"
            fill="url(#boardG2)" stroke="rgba(255,255,255,0.9)" strokeWidth="3" />
          <rect x="118" y="78" width="64" height="48" rx="2"
            fill="none" stroke="#FF3B3B" strokeWidth="3" opacity="0.95" />
          <rect x="46" y="24" width="80" height="6" fill="rgba(255,255,255,0.35)" />
        </motion.g>

        {/* RIM */}
        <motion.g
          animate={!won && state === "result" ? { scaleX: [1, 1.12, 1], scaleY: [1, 0.9, 1] } : won && state === "result" ? { scaleY: [1, 1.08, 1] } : {}}
          transition={{ duration: 0.3 }}
          style={{ transformOrigin: "150px 160px" }}
        >
          <rect x="138" y="150" width="24" height="6" fill="#8A2A05" />
          <ellipse cx="150" cy="158" rx="52" ry="7" fill="url(#rimG2)" stroke="#4a1203" strokeWidth="1.5" />
          <ellipse cx="150" cy="158" rx="48" ry="5" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
        </motion.g>

        {/* NET */}
        <motion.g
          animate={won && state === "result" ? { y: [0, -3, 2, -2, 0] } : {}}
          transition={{ duration: 0.55 }}
        >
          {Array.from({ length: 13 }).map((_, i) => {
            const topX = 100 + i * 8.5;
            const botX = 128 + i * 3.5;
            return (
              <line
                key={`v${i}`}
                x1={topX} y1="158"
                x2={botX} y2="226"
                stroke="rgba(255,255,255,0.78)" strokeWidth="1"
              />
            );
          })}
          {[0, 1, 2, 3].map((i) => {
            const y = 170 + i * 14;
            const left = 102 + i * 4;
            const right = 198 - i * 4;
            const mid = (left + right) / 2;
            return (
              <path
                key={`h${i}`}
                d={`M ${left} ${y} Q ${mid} ${y + 6} ${right} ${y}`}
                fill="none"
                stroke="rgba(255,255,255,0.55)"
                strokeWidth="1"
              />
            );
          })}
        </motion.g>
      </svg>

      {/* ---- PLAYER (positioned at START, scales with scene) ---- */}
      <motion.div
        animate={state === "playing" ? { y: [0, -16, 0], rotate: [0, -2, 2, 0] } : state === "idle" ? { y: [0, -2, 0] } : {}}
        transition={state === "playing" ? { duration: 0.6, ease: "easeOut" } : state === "idle" ? { duration: 2, repeat: Infinity } : {}}
        className="absolute"
        style={{ left: START.x - 65, top: START.y - 150 }}
      >
        <svg width="130" height="200" viewBox="0 0 110 170">
          <defs>
            <linearGradient id="hpKit2" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#FF2E6B" /><stop offset="0.6" stopColor="#C9004F" /><stop offset="1" stopColor="#7a002f" />
            </linearGradient>
            <linearGradient id="hpSkin2" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#eac29a" /><stop offset="1" stopColor="#b88d6a" />
            </linearGradient>
          </defs>
          <ellipse cx="55" cy="166" rx="22" ry="4" fill="rgba(0,0,0,0.5)" />
          <path d="M 46 94 L 42 148 Q 42 160 48 162 L 56 162 Q 58 160 56 148 L 54 94 Z" fill="url(#hpKit2)" />
          <motion.g animate={state === "playing" ? { rotate: -14 } : { rotate: 0 }} transition={{ duration: 0.3 }} style={{ transformOrigin: "60px 94px" }}>
            <path d="M 58 94 L 60 148 Q 60 160 66 162 L 74 162 Q 76 160 74 148 L 72 94 Z" fill="url(#hpKit2)" />
            <ellipse cx="70" cy="164" rx="10" ry="3" fill="#FFF" />
          </motion.g>
          <ellipse cx="50" cy="164" rx="10" ry="3" fill="#FFF" />
          <path d="M 36 78 L 74 78 L 76 102 Q 68 104 55 104 Q 42 104 34 102 Z" fill="url(#hpKit2)" />
          <path d="M 30 42 Q 34 34 44 32 L 58 32 Q 70 34 74 42 L 76 80 Q 66 84 55 84 Q 36 84 28 80 Z" fill="url(#hpKit2)" />
          <text x="55" y="62" fontSize="18" fontWeight="900" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontFamily="Impact, Arial Black">23</text>
          <motion.g
            animate={state === "playing" ? { rotate: -45 } : { rotate: -8 }}
            transition={{ duration: 0.3 }}
            style={{ transformOrigin: "30px 44px" }}
          >
            <path d="M 30 42 Q 20 42 12 38 Q 6 36 4 40 L 8 46 Q 16 48 24 50 Q 30 52 34 48 Z" fill="url(#hpKit2)" />
            <ellipse cx="6" cy="42" rx="5" ry="4" fill="url(#hpSkin2)" />
          </motion.g>
          <motion.g
            animate={state === "playing" ? { rotate: -110 } : state !== "idle" ? { rotate: -110 } : { rotate: -40 }}
            transition={{ duration: 0.32 }}
            style={{ transformOrigin: "74px 44px" }}
          >
            <path d="M 74 42 Q 86 36 94 28 Q 100 22 102 26 L 96 32 Q 90 40 84 46 Q 80 50 76 48 Z" fill="url(#hpKit2)" />
            <path d="M 100 20 Q 108 18 110 24 Q 110 28 104 30 Q 98 30 96 26 Z" fill="url(#hpSkin2)" />
          </motion.g>
          <ellipse cx="55" cy="22" rx="11" ry="12" fill="url(#hpSkin2)" />
          <path d="M 44 18 Q 46 8 55 8 Q 64 8 66 18 Q 64 14 58 12 Q 50 12 44 18 Z" fill="#2a1708" />
          <circle cx="51" cy="22" r="1.3" fill="#111" />
          <circle cx="59" cy="22" r="1.3" fill="#111" />
          <path d="M 51 28 Q 55 30 59 28" stroke="#111" strokeWidth="0.8" fill="none" />
        </svg>
      </motion.div>

      {/* ---- BASKETBALL — starts at exact START point; animates to RIM ---- */}
      <motion.div
        initial={false}
        animate={ballAnim}
        transition={ballTransition}
        className="absolute z-20"
        style={{ left: START.x - 20, top: START.y - 20 }}
      >
        <svg width="40" height="40" viewBox="0 0 36 36">
          <defs>
            <radialGradient id="hball3" cx="0.35" cy="0.35" r="0.75">
              <stop offset="0" stopColor="#FFB37A" /><stop offset="0.6" stopColor="#F08020" /><stop offset="1" stopColor="#8A3C06" />
            </radialGradient>
          </defs>
          <circle cx="18" cy="18" r="15" fill="url(#hball3)" stroke="#4a1a06" strokeWidth="1.5" />
          <path d="M 18 3 Q 18 18 18 33" stroke="#4a1a06" strokeWidth="1.5" fill="none" />
          <path d="M 3 18 Q 18 18 33 18" stroke="#4a1a06" strokeWidth="1.5" fill="none" />
          <path d="M 5 7 Q 18 18 31 7" stroke="#4a1a06" strokeWidth="1.5" fill="none" />
          <path d="M 5 29 Q 18 18 31 29" stroke="#4a1a06" strokeWidth="1.5" fill="none" />
        </svg>
      </motion.div>

      {/* swish particles on win — burst at rim center */}
      <AnimatePresence>
        {won && state === "result" && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute pointer-events-none"
            style={{ left: RIM.x, top: RIM.y + 30 }}
          >
            {[...Array(10)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                animate={{ x: Math.cos(i * Math.PI / 5) * 60, y: Math.sin(i * Math.PI / 5) * 55 + 20, opacity: 0, scale: 0.4 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="absolute h-1.5 w-1.5 rounded-full bg-white"
                style={{ left: 0, top: 0, boxShadow: "0 0 8px rgba(255,255,255,0.9)" }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {state === "result" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
          className={`absolute top-4 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full font-display text-2xl z-30 ${won ? "bg-[color:var(--naevo-success)]/20 text-[color:var(--naevo-success)] border border-[color:var(--naevo-success)]/50" : "bg-[color:var(--naevo-danger)]/20 text-[color:var(--naevo-danger)] border border-[color:var(--naevo-danger)]/50"}`}
        >{won ? "SWISH!" : "BRICK!"}</motion.div>
      )}
    </div>
  );
}
/* ---------- Treasure Duel ---------- */
function TreasureScene({ state, choice, result }) {
  const actual = result?.detail?.pick || choice;
  const won = result?.won;
  const Chest = ({ side, isGold, opened, picked }) => (
    <motion.div
      animate={
        state === "playing" ? { y: [0, -4, 0, 4, 0], rotate: [0, -2, 2, 0] } :
        opened && isGold ? { y: [-5, -40], scale: [1, 1.1] } :
        opened && !isGold ? { opacity: 0.4, scale: 0.95 } : {}
      }
      transition={{ duration: state === "playing" ? 0.2 : 0.4, repeat: state === "playing" ? Infinity : 0 }}
      className="relative"
    >
      {opened && isGold && (
        <>
          <motion.div
            initial={{ scale: 0, opacity: 0.8 }} animate={{ scale: 3, opacity: 0 }} transition={{ duration: 1.5 }}
            className="absolute inset-0 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(255,215,0,0.6) 0%, transparent 70%)", filter: "blur(10px)" }}
          />
          <motion.div initial={{ y: 0, opacity: 1 }} animate={{ y: -80, opacity: 0 }} transition={{ duration: 1.4 }} className="absolute left-1/2 -translate-x-1/2 top-0 text-4xl">🏆</motion.div>
        </>
      )}
      <div
        className="w-32 h-24 rounded-lg relative border-2"
        style={{
          background: opened && isGold ? "linear-gradient(180deg, #FFD700, #B8860B)" : "linear-gradient(180deg, #6b3a1a 0%, #3a1f0a 100%)",
          borderColor: picked ? "#E8B54A" : "rgba(255,255,255,0.15)",
          boxShadow: picked ? "0 0 20px rgba(232,181,74,0.4)" : (opened && isGold ? "0 0 50px rgba(255,215,0,0.7)" : "none"),
        }}
      >
        <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-2 bg-[#2a1708]" />
        <div className="absolute left-0 right-0 top-1/3 h-1.5 bg-[#2a1708]" />
        <div className="absolute left-1/2 -translate-x-1/2 top-1/3 translate-y-0 h-5 w-5 rounded-sm bg-[#FFD700] border border-[#8a6b00]" />
        <motion.div
          animate={opened ? { rotateX: -110, y: -6 } : {}} transition={{ duration: 0.5 }}
          style={{ transformOrigin: "top", background: opened && isGold ? "linear-gradient(180deg, #FFD700, #daa520)" : "linear-gradient(180deg, #7a4321 0%, #4a2810 100%)" }}
          className="absolute top-0 left-0 right-0 h-10 rounded-t-lg border-b-2 border-[#2a1708]"
        >
          <div className="absolute left-1/2 -translate-x-1/2 top-1 h-2 w-10 bg-[#2a1708] rounded" />
        </motion.div>
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono-naevo text-white/60 uppercase">{side}</div>
      </div>
    </motion.div>
  );
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: "radial-gradient(ellipse at center, #1f1208 0%, #0a0603 70%, #000 100%)" }}>
      <svg viewBox="0 0 600 380" className="absolute inset-0 w-full h-full">
        <defs>
          <linearGradient id="stone" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="transparent" />
            <stop offset="1" stopColor="rgba(80,55,25,0.5)" />
          </linearGradient>
        </defs>
        <rect y="260" width="600" height="120" fill="url(#stone)" />
        <radialGradient id="torch" cx="0.5" cy="0.3" r="0.4">
          <stop offset="0" stopColor="rgba(255,160,30,0.3)" />
          <stop offset="1" stopColor="transparent" />
        </radialGradient>
        <rect width="600" height="380" fill="url(#torch)" />
      </svg>
      <motion.div animate={{ opacity: [0.7, 1, 0.8, 1, 0.7] }} transition={{ duration: 0.4, repeat: Infinity }} className="absolute left-6 top-10 text-4xl">🔥</motion.div>
      <motion.div animate={{ opacity: [1, 0.7, 1, 0.8, 1] }} transition={{ duration: 0.5, repeat: Infinity }} className="absolute right-6 top-10 text-4xl">🔥</motion.div>
      <div className="absolute inset-0 flex items-center justify-center gap-20 pt-4">
        <Chest side="left" isGold={actual === "left"} opened={state === "result"} picked={choice === "left"} />
        <Chest side="right" isGold={actual === "right"} opened={state === "result"} picked={choice === "right"} />
      </div>
      {state === "idle" && (
        <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 2, repeat: Infinity }} className="absolute top-16 left-1/2 -translate-x-1/2 text-3xl text-magenta">❓</motion.div>
      )}
      {state === "result" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
          className={`absolute top-6 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full font-display text-2xl ${won ? "bg-[color:var(--naevo-success)]/20 text-[color:var(--naevo-success)] border border-[color:var(--naevo-success)]/50" : "bg-[color:var(--naevo-danger)]/20 text-[color:var(--naevo-danger)] border border-[color:var(--naevo-danger)]/50"}`}
        >{won ? "GOLD!" : "EMPTY!"}</motion.div>
      )}
    </div>
  );
}

/* ---------- Confetti ---------- */
/* ---------- Dice Duel ---------- */
function DiceScene({ state, choice, result }) {
  const won = !!result?.won;
  // Pick a plausible digit based on actual pick (not just win/loss) - memoized per round
  const digit = useMemo(() => {
    const pickSide = result?.detail?.pick || choice;
    const pool = pickSide === "even" ? [2, 4, 6] : [1, 3, 5];
    const other = pickSide === "even" ? [1, 3, 5] : [2, 4, 6];
    return won ? pool[Math.floor(Math.random() * pool.length)] : other[Math.floor(Math.random() * other.length)];
  }, [won, choice, result]);
  const Pip = ({ cx, cy }) => <circle cx={cx} cy={cy} r="6" fill="#111" />;
  const facePips = { 1: [[32, 32]], 2: [[18, 18], [46, 46]], 3: [[18, 18], [32, 32], [46, 46]], 4: [[18, 18], [46, 18], [18, 46], [46, 46]], 5: [[18, 18], [46, 18], [32, 32], [18, 46], [46, 46]], 6: [[18, 18], [46, 18], [18, 32], [46, 32], [18, 46], [46, 46]] };
  const pips = facePips[digit] || facePips[1];
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: "radial-gradient(ellipse at center, #1a0a30 0%, #050208 70%)" }}>
      <svg viewBox="0 0 600 380" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        {/* casino felt */}
        <defs>
          <radialGradient id="feltD" cx="0.5" cy="0.5" r="0.6">
            <stop offset="0" stopColor="rgba(0,255,157,0.2)" /><stop offset="1" stopColor="transparent" />
          </radialGradient>
        </defs>
        <rect width="600" height="380" fill="url(#feltD)" />
        {[...Array(40)].map((_, i) => (
          <circle key={i} cx={30 + (i * 37) % 580} cy={20 + Math.floor(i / 15) * 340} r="1.5" fill="rgba(255,255,255,0.1)" />
        ))}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center" style={{ perspective: 800 }}>
        <motion.div
          animate={state === "playing" ? { rotateX: [0, 180, 360, 540, 720], rotateY: [0, 180, 360, 540, 720], y: [0, -30, 0, -20, 0] } : state === "result" ? { rotateX: 0, rotateY: 0 } : {}}
          transition={state === "playing" ? { duration: 1.1, ease: "easeOut" } : {}}
          className="relative"
          style={{ width: 128, height: 128, transformStyle: "preserve-3d" }}
        >
          <div className="absolute inset-0 rounded-xl flex items-center justify-center" style={{
            background: "linear-gradient(135deg, #fff 0%, #e0e0e0 100%)",
            border: "3px solid rgba(0,0,0,0.2)",
            boxShadow: state === "result" ? "0 0 50px rgba(232,181,74,0.6)" : "0 10px 30px rgba(0,0,0,0.5)",
          }}>
            <svg width="64" height="64" viewBox="0 0 64 64">
              {pips.map(([cx, cy], i) => <Pip key={i} cx={cx} cy={cy} />)}
            </svg>
          </div>
        </motion.div>
      </div>
      {state === "result" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
          className={`absolute top-6 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full font-display text-2xl ${won ? "bg-[color:var(--naevo-success)]/20 text-[color:var(--naevo-success)] border border-[color:var(--naevo-success)]/50" : "bg-[color:var(--naevo-danger)]/20 text-[color:var(--naevo-danger)] border border-[color:var(--naevo-danger)]/50"}`}
        >Rolled {digit} · {won ? "WIN" : "LOSS"}</motion.div>
      )}
    </div>
  );
}

/* ---------- Lucky Wheel ---------- */
function WheelScene({ state, choice, result }) {
  const won = !!result?.won;
  const pickSide = result?.detail?.pick || choice;
  // 10 slots alternating red/green. Even indices = green (EVEN), odd = red (ODD).
  const slots = Array.from({ length: 10 }, (_, i) => ({ idx: i, color: i % 2 === 0 ? "#00FF9D" : "#FF3366" }));
  // Lock target per round so it doesn't re-roll on re-renders
  const target = useMemo(() => {
    const greenSlots = [0, 2, 4, 6, 8];
    const redSlots = [1, 3, 5, 7, 9];
    const want = pickSide === "green" ? (won ? greenSlots : redSlots) : (won ? redSlots : greenSlots);
    return want[Math.floor(Math.random() * want.length)];
  }, [won, pickSide, result]);
  // Degrees per slot = 36. Wheel settles so the pointer (at top) lands on `target`.
  const settleDeg = 1800 + (360 - target * 36);
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: "radial-gradient(ellipse at center, #2a0a30 0%, #050208 70%)" }}>
      {/* pointer */}
      <div className="absolute left-1/2 top-6 -translate-x-1/2 z-10">
        <svg width="24" height="30" viewBox="0 0 24 30">
          <polygon points="12,30 2,2 22,2" fill="#F5A623" stroke="#111" strokeWidth="1.5" />
        </svg>
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          animate={state === "playing" ? { rotate: settleDeg } : state === "result" ? { rotate: settleDeg } : { rotate: 0 }}
          transition={state === "playing" ? { duration: 2.2, ease: [0.18, 0.8, 0.2, 1] } : {}}
          style={{ width: 260, height: 260 }}
        >
          <svg width="260" height="260" viewBox="-130 -130 260 260">
            <circle r="128" fill="#0a0518" stroke="#F5A623" strokeWidth="3" />
            {slots.map((s) => {
              const a1 = (s.idx * 36 - 90) * Math.PI / 180;
              const a2 = ((s.idx + 1) * 36 - 90) * Math.PI / 180;
              const x1 = Math.cos(a1) * 120, y1 = Math.sin(a1) * 120;
              const x2 = Math.cos(a2) * 120, y2 = Math.sin(a2) * 120;
              const am = ((s.idx + 0.5) * 36 - 90) * Math.PI / 180;
              const tx = Math.cos(am) * 85, ty = Math.sin(am) * 85;
              return (
                <g key={s.idx}>
                  <path d={`M 0 0 L ${x1} ${y1} A 120 120 0 0 1 ${x2} ${y2} Z`} fill={s.color} fillOpacity="0.85" stroke="#111" strokeWidth="1.5" />
                  <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" fontSize="18" fontWeight="900" fill="#111" fontFamily="Impact, Arial Black">{s.idx}</text>
                </g>
              );
            })}
            <circle r="18" fill="#F5A623" stroke="#111" strokeWidth="2" />
          </svg>
        </motion.div>
      </div>
      {state === "result" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
          className={`absolute top-6 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full font-display text-2xl ${won ? "bg-[color:var(--naevo-success)]/20 text-[color:var(--naevo-success)] border border-[color:var(--naevo-success)]/50" : "bg-[color:var(--naevo-danger)]/20 text-[color:var(--naevo-danger)] border border-[color:var(--naevo-danger)]/50"}`}
        >Slot {target} · {won ? "WIN" : "LOSS"}</motion.div>
      )}
    </div>
  );
}


function Confetti() {
  const pieces = Array.from({ length: 28 });
  const colors = ["#E8B54A", "#00FF9D", "#B28DFF", "#F5A623", "#FFFFFF"];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {pieces.map((_, i) => {
        const x = Math.random() * 100;
        const delay = Math.random() * 0.4;
        const duration = 1.2 + Math.random() * 0.8;
        const rotate = Math.random() * 720;
        const color = colors[i % colors.length];
        return (
          <motion.div
            key={i}
            initial={{ y: -40, x: `${x}%`, opacity: 0, rotate: 0 }}
            animate={{ y: 400, opacity: [0, 1, 1, 0], rotate }}
            transition={{ duration, delay, ease: "easeOut" }}
            className="absolute h-3 w-1.5 rounded-sm"
            style={{ background: color, left: 0, top: 0 }}
          />
        );
      })}
    </div>
  );
}

/* ---------- Card previews ---------- */
function AccountChip() {
  const auth = tradeAuth();
  if (!auth) {
    return <button onClick={() => connectToDeriv()} data-testid="arcade-login-chip" className="text-[10px] uppercase tracking-widest text-gold underline">Login with Deriv to play</button>;
  }
  return (
    <div data-testid="arcade-account-chip" className={`text-[10px] uppercase tracking-widest font-mono-naevo ${auth.type === "demo" ? "text-[#2AABEE]" : "text-gold"}`}>
      Playing on {auth.type === "demo" ? "DEMO" : "REAL"} · {auth.account_id}
    </div>
  );
}

function CardArt({ gameKey }) {
  return (
    <>
      <img src={GAME_COVERS[gameKey]} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/5" />
    </>
  );
}
